import { History, Pencil, Plus, Star } from "lucide-react";
import { Fragment, useState } from "react";
import { useAuth } from "react-oidc-context";
import { apiGet, apiSend, useApi } from "../api";
import type { PortalConfig } from "../config";
import { dateTime, money } from "../format";
import { ListsSwitch } from "./StatusesPage";

interface Svc { id: string; category: string | null; name: string; description: string | null; price: string; cost: string | null;
  isFavorite: boolean; isActive: boolean; updatedAt: string; timesUsed: number; lastUsed: string | null }
interface Change { changedAt: string; oldPrice: string | null; newPrice: string; oldCost: string | null; newCost: string | null; by: string | null }

const blank = { name: "", description: "", category: "", price: "", isFavorite: false, isActive: true };
type Form = typeof blank;
const formOf = (s: Svc): Form => ({ name: s.name, description: s.description ?? "", category: s.category ?? "", price: s.price,
  isFavorite: s.isFavorite, isActive: s.isActive });

/**
 * Services (owner, 2026-11-04): the price list. Services carry no cost: costs are ticket expenses. Favorites show first on a ticket's Services card; retired
 * services stay on old tickets. Every price or cost change is kept, with who changed it. Tickets keep the
 * price they were quoted.
 */
export function ServicesPage({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [reload, setReload] = useState(0);
  const { data, error } = useApi<{ services: Svc[] }>(config, "/services", reload);
  const [filter, setFilter] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<Form>(blank);
  const [history, setHistory] = useState<{ id: string; rows: Change[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [saved, setSaved] = useState("");

  const rows = (data?.services ?? []).filter((s) => (showRetired || s.isActive)
    && (!filter.trim() || `${s.name} ${s.description ?? ""} ${s.category ?? ""}`.toLowerCase().includes(filter.trim().toLowerCase())));
  const save = async () => {
    setBusy(true); setProblem("");
    try {
      await apiSend(config, token, editing === "new" ? "POST" : "PUT", editing === "new" ? "/services" : `/services/${editing}`, form);
      setSaved(`Saved ${form.name}. Open tickets show the new list after a reload.`);
      setEditing(null); setReload((r) => r + 1);
    } catch (e) { setProblem((e as Error).message); } finally { setBusy(false); }
  };
  const showHistory = async (id: string) => {
    if (history?.id === id) { setHistory(null); return; }
    const r = await apiGet<{ history: Change[] }>(config, token, `/services/${id}/history`);
    setHistory({ id, rows: r.history });
  };
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const editor = (
    <tr className="editing"><td colSpan={6}>
      <div className="svc-form no-cost">
        <label>Name<input value={form.name} onChange={set("name")} autoFocus /></label>
        <label>Category<input value={form.category} onChange={set("category")} /></label>
        <label>Price ($)<input type="number" min={0} step="0.01" value={form.price} onChange={set("price")} /></label>
        <label className="wide">Description (shown to the customer on the estimate)<input value={form.description} onChange={set("description")} /></label>
        <label className="check"><input type="checkbox" checked={form.isFavorite} onChange={set("isFavorite")} /> Favorite</label>
        <label className="check"><input type="checkbox" checked={form.isActive} onChange={set("isActive")} /> Active</label>
        <div className="btns"><button type="button" className="tb-btn primary" disabled={busy} onClick={() => void save()}>Save</button>
          <button type="button" className="tb-btn" onClick={() => { setEditing(null); setProblem(""); }}>Cancel</button></div>
        {problem && <p className="error">{problem}</p>}
      </div>
    </td></tr>
  );

  return (
    <div className="admin-page">
      <div className="admin-head">
        <h2>Services</h2>
        <ListsSwitch on="services" />
        <input type="search" placeholder="Filter services…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter services" />
        <label className="check"><input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired</label>
        <button type="button" className="tb-btn primary" onClick={() => { setForm(blank); setEditing("new"); setProblem(""); }}><Plus size={15} />Add service</button>
      </div>
      {saved && <p className="ok">{saved}</p>}
      {error && <p className="error">{error.message}</p>}
      <table className="t-plain admin">
        <thead><tr><th></th><th>Service</th><th>Category</th><th className="num">Price</th><th className="num">Used</th><th></th></tr></thead>
        <tbody>
          {editing === "new" && editor}
          {rows.map((s) => (
            <Fragment key={s.id}>
              {editing === s.id ? editor : (
                <tr className={s.isActive ? "" : "retired"}>
                  <td>{s.isFavorite && <Star size={15} className="fav" aria-label="Favorite" />}</td>
                  <td><strong>{s.name}</strong>{!s.isActive && <span className="tag">retired</span>}{s.description && <div className="hint">{s.description}</div>}</td>
                  <td>{s.category}</td>
                  <td className="num">{money(s.price)}</td>
                  <td className="num" title={s.lastUsed ? `Last used ${dateTime(s.lastUsed)}` : "Never used"}>{s.timesUsed.toLocaleString()}</td>
                  <td className="nowrap">
                    <button type="button" className="icon-link" aria-label={`Edit ${s.name}`} onClick={() => { setForm(formOf(s)); setEditing(s.id); setProblem(""); setSaved(""); }}><Pencil size={15} /></button>
                    <button type="button" className="icon-link" aria-label={`Price history of ${s.name}`} title="Price history" onClick={() => void showHistory(s.id)}><History size={15} /></button>
                  </td>
                </tr>
              )}
              {history?.id === s.id && (
                <tr className="sub"><td colSpan={6}>
                  {history.rows.length ? history.rows.map((h, i) => (
                    <div key={i}>{dateTime(h.changedAt)} · price {h.oldPrice ? money(h.oldPrice) : "—"} → {money(h.newPrice)}
                      {h.by && <> · {h.by}</>}</div>
                  )) : <span className="hint">No changes recorded.</span>}
                </td></tr>
              )}
            </Fragment>
          ))}
          {data && !rows.length && <tr><td colSpan={6} className="hint">No services match.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
